/**
 * Fetch wrapper for /api/*.
 *
 * Handles the native 401 by bouncing to the Entra login. We deliberately do NOT
 * use responseOverrides.401 in staticwebapp.config.json — that turns the 401
 * into a 302 the browser follows into login.microsoftonline.com, which fails
 * CORS and surfaces as an unhelpful "TypeError: Failed to fetch".
 */
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (res.status === 401) {
    const returnTo = window.location.pathname + window.location.search
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`
    // Never resolves — the navigation takes over.
    return new Promise<T>(() => {})
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      // non-JSON error body; keep the generic message
    }
    throw new ApiError(res.status, message)
  }

  return (await res.json()) as T
}

export type Role = 'admin' | 'reader' | 'none'

export interface MeResponse {
  upn: string
  displayName: string | null
  role: Role
  isAdmin: boolean
  hasAccess: boolean
  /** Route slugs this user must not see. App-level tab visibility (not Entra). */
  hiddenTabs: string[]
}

export interface FinanceTab { key: string; label: string }

/** Admin → Users → Tabs popup: the tab catalogue + this user's hidden set. */
export const getUserTabs = (id: number) =>
  apiFetch<{ tabs: FinanceTab[]; hidden: string[] }>(`/api/users/${id}/tabs`)

/** Replace a user's hidden tab set. */
export const saveUserTabs = (id: number, hidden: string[]) =>
  apiFetch<{ ok: true }>(`/api/users/${id}/tabs`, {
    method: 'PUT',
    body: JSON.stringify({ hidden }),
  })

export interface AppUser {
  id: number
  upn: string
  display_name: string | null
  role: Role
  first_seen_utc: string
  last_seen_utc: string | null
  updated_by: string | null
  updated_utc: string | null
}
